import logger from 'app/utils/logger.ts';

// WebSocket event constants
const WS_EVENT_PING = 'ping';
const WS_EVENT_PONG = 'pong';
const WS_CLOSE_REASON = 'Normal closure after pong test';
const WS_CLOSE_CODE = 1000;

// Interface definitions for message types
interface PongMessage {
  event: typeof WS_EVENT_PONG;
  // Add other fields if the server sends more
}

interface PingMessage {
  event: typeof WS_EVENT_PING;
}

// Extended WebSocket interface with userId
interface ExtendedWebSocket extends WebSocket {
  userId?: string;
}

let wsInstance: ExtendedWebSocket | null = null;

const MAX_RECONNECT_DELAY = 30000; // 30 seconds - Note: Reconnection logic is currently commented out/not used for the test connection.
const MAX_RECONNECT_ATTEMPTS = 3; // Maximum number of reconnection attempts - Note: Reconnection logic is currently commented out/not used for the test connection.
let reconnectAttempts = 0;

// Flag to ensure we only attempt to connect once during the app's lifecycle
let hasInitiatedConnection = false;
// Flag to track if the closure was initiated by our client code
let isIntentionalClose = false;

export const connectVadWebsocket = (userId?: string) => {
  // Reset intentional close flag at the beginning of a connection attempt
  isIntentionalClose = false;

  // If we've already initiated a connection attempt during this app session, don't try again
  if (hasInitiatedConnection) {
    logger.log('VAD WebSocket connection already initiated in this session');
    return wsInstance;
  }

  // Mark that we've initiated a connection
  hasInitiatedConnection = true;

  if (wsInstance && (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING)) {
    logger.log('VAD WebSocket already connected or connecting');
    return wsInstance;
  }

  const wsUrl = process.env.REACT_APP_VAD_WS || 'wss://api.sklls.ai';

  try {
    logger.log('Attempting VAD WebSocket test connection to:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      logger.log('VAD WebSocket connection opened for test');
      reconnectAttempts = 0;
      // Send ping using the current WebSocket instance
      setTimeout(() => sendPing(ws), 400);      
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as PongMessage;
        if (message && message.event === WS_EVENT_PONG) {
          logger.log('VAD WebSocket connection test succeeded', { userId: wsInstance?.userId });
          // Close the connection intentionally after successful ping-pong test
          disconnectVadWebsocket();
        } else {
          logger.warn('Received unexpected WebSocket message structure:', message);
        }
      } catch (e) {
        logger.error('Failed to parse incoming message as JSON:', event.data, e);
      }
    };

    ws.onclose = (event) => {
      const wasIntentional = isIntentionalClose; // Capture the flag state
      isIntentionalClose = false; // Reset flag
      wsInstance = null; // Always nullify the instance on close

      if (wasIntentional) {
        logger.log(`VAD WebSocket connection closed intentionally. Code: ${event.code}, Reason: ${event.reason}`);
      } else {
        // Handle unexpected closure
        if (!event.wasClean) {
          // Code 1006 is common for abnormal closures (e.g., network issues, server crash without proper close frame)
          logger.warn(`VAD WebSocket closed unexpectedly. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
        } else {
          // This case might occur if the server closes cleanly but unexpectedly from the client's perspective
          logger.log(`VAD WebSocket connection closed by server. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
        }
        // Only log test failed if the closure was not the intentional one after pong
        logger.log('VAD WebSocket connection test failed', { userId: wsInstance?.userId });
        // Note: Reconnection logic would go here if needed for a persistent connection,
        // but it's not required for this simple ping-pong test.
      }
    };

    ws.onerror = (error) => {
      // An error event will often be followed by a close event (usually unclean)
      logger.error('VAD WebSocket test connection error:', error);
      // wsInstance might still be connecting or open here, onclose will handle cleanup.
    };

    wsInstance = ws as ExtendedWebSocket;
    if (userId) {
      wsInstance.userId = userId;
    }
    return wsInstance;
  } catch (error) {
    logger.error('Failed to create VAD WebSocket:', error);
    hasInitiatedConnection = false; // Allow retrying if creation failed immediately
    return null;
  }
};

export const disconnectVadWebsocket = () => {
  if (wsInstance && wsInstance.readyState === WebSocket.OPEN) {
    logger.log('Intentionally closing VAD WebSocket connection after test...');
    isIntentionalClose = true; // Set flag before closing
    wsInstance.close(WS_CLOSE_CODE, WS_CLOSE_REASON);
    // wsInstance = null; // Let onclose handler manage setting instance to null
  } else if (wsInstance && wsInstance.readyState === WebSocket.CONNECTING) {
      logger.log('Intentionally closing VAD WebSocket connection while it was still connecting...');
      isIntentionalClose = true; // Set flag before closing
      wsInstance.close(WS_CLOSE_CODE, WS_CLOSE_REASON);
  } else {
      logger.log('VAD WebSocket already closed or null, no action needed for disconnect.');
  }
};

export const getVadWebsocket = () => wsInstance;

export const sendPing = (ws?: WebSocket | ExtendedWebSocket) => {
  const socket = ws || wsInstance;
  if (socket && socket.readyState === WebSocket.OPEN) {
    const pingMessage: PingMessage = {
      event: WS_EVENT_PING
    };

    try {
      logger.log('Sending ping to VAD WebSocket server');
      socket.send(JSON.stringify(pingMessage));
      return true;
    } catch (error) {
      logger.error('Error sending ping message:', error);
      return false;
    }
  } else {
    logger.warn('Cannot send ping, WebSocket is not open. State:', socket?.readyState);
    return false;
  }
};
